/**
 * @prettier
 * @flow
 */
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { Link, navigate } from '@reach/router';
import moment from 'moment';
import MUIDataTable from 'mui-datatables';
import React, { useEffect, useRef, useState } from 'react';
import { paymentLinkApi } from 'src/api/paymentLinkApi';
import { CancelButton } from 'src/components/buttons/CancelButton';
import { CopyToClipboardButton } from 'src/components/buttons/CopyToClipboardButton';
import { CreateToolbarButton } from 'src/components/buttons/CreateToolbarButton';
import { RefreshToolbarButton } from 'src/components/buttons/RefreshToolbarButton';
import { CancelPaymentLinkDialog } from 'src/components/CancelPaymentLinkDialog';
import { UpdatingContentProgress } from 'src/components/UpdatingContentProgress';
import { PaymentLinkStatuses } from 'src/constants/PaymentLinkStatus';
import type { AppContextVm } from 'src/types/AppContextVm';
import type { PaymentLinkId } from 'src/types/Id';
import type { PaymentLinkVm } from 'src/types/PaymentLinkVm';
import { alertKnownErrorOrSomethingWentWrong } from 'src/utils/alert/alertKnownErrorOrSomethingWentWrong';
import { formatAsReadableDateTime } from 'src/utils/date/formatAsReadableDateTime';
import { translatedTextLabels } from 'src/utils/mui-datatables/translatedTextLabels';
import { formatAsNumberCurrency } from 'src/utils/number/formatAsNumberCurrency';
import { useSelector } from 'src/utils/react/useSelector';
import { translate } from 'src/utils/translate/translate';

export function PaymentLinksPage() {
    const classes = useStyles();
    const table = useRef(null);
    const [loading, setLoading] = useState(false);
    const [appContext, setAppContext] = useState(((undefined: any): AppContextVm));
    const [paymentLinks, setPaymentLinks] = useState<Array<PaymentLinkVm>>([]);
    const [cancelPaymentLinkDialogState, setCancelPaymentLinkDialogState] = useState({ open: false, paymentLink: undefined });
    const accountId = useSelector(state => state.app.accountId);

    let refreshTimout;
    const refreshEvery30Second = async () => {
        refreshTimout = setTimeout(() => {
            load();
            refreshEvery30Second();
        }, 5 * 1000); // 30 seconds
    };

    useEffect(() => {
        load();
        refreshEvery30Second();
        return () => {
            clearTimeout(refreshTimout);
        };
    }, []);

    const load = async () => {
        setLoading(true);

        const responseAppContext = await paymentLinkApi.getAppContext({ accountId });
        if (!responseAppContext.ok) {
            setLoading(false);
            alertKnownErrorOrSomethingWentWrong(responseAppContext);
            return;
        }
        setAppContext(responseAppContext.data);

        const responsePaymentLinks = await paymentLinkApi.getPaymentLinks({ accountId });
        if (!responsePaymentLinks.ok) {
            setLoading(false);
            alertKnownErrorOrSomethingWentWrong(responsePaymentLinks);
            return;
        }

        setLoading(false);
        setPaymentLinks(responsePaymentLinks.data);
    };

    const columns = [
        {
            name: 'paymentLinkId',
            label: 'paymentLinkId',
            options: {
                display: 'excluded',
                filter: false,
                searchable: true,
            },
        },
        {
            name: 'createdAt',
            label: translate('Created'),
            options: {
                filter: false,
                searchable: true,
            },
        },
        {
            name: 'paymentLinkStatus',
            label: translate('Status'),
            options: {
                filter: true,
                searchable: false,
            },
        },
        {
            name: 'reason',
            label: translate('Payment Reason'),
            options: {
                filter: false,
                searchable: true,
            },
        },
        {
            name: 'customerName',
            label: translate('Customer Name'),
            options: {
                filter: false,
                searchable: true,
            },
        },
        {
            name: 'total',
            label: translate('Total Amount'),
            options: {
                filter: false,
                searchable: false,
            },
        },
        {
            name: 'url',
            label: ' ',
            options: {
                filter: false,
                sort: false,
                customBodyRender: url => {
                    return <CopyToClipboardButton text={url} tooltip={translate('Copy Payment Link')} />;
                },
                setCellProps: () => {
                    return {
                        style: { paddingLeft: 0, paddingRight: 0 },
                    };
                },
            },
        },
        {
            name: 'paymentLinkId',
            label: ' ',
            options: {
                filter: false,
                sort: false,
                customBodyRender: paymentLinkId => {
                    const paymentLink: PaymentLinkVm = (paymentLinks.find(paymentLink => paymentLink.paymentLinkId === paymentLinkId): any);
                    switch (getPaymentLinkCancelType(paymentLink)) {
                        case 'CANCEL_PAYMENT_LINK':
                            return (
                                <CancelButton
                                    onClick={async event => {
                                        event.stopPropagation();
                                        setCancelPaymentLinkDialogState({ open: true, paymentLink: paymentLinks.find(paymentLink => paymentLink.paymentLinkId === paymentLinkId) });
                                    }}
                                    tooltip={translate('Cancel Payment Link')}
                                />
                            );
                        case 'CANCEL_PAYMENT':
                            return (
                                <Button
                                    onClick={async event => {
                                        event.stopPropagation();
                                        setCancelPaymentLinkDialogState({ open: true, paymentLink: paymentLinks.find(paymentLink => paymentLink.paymentLinkId === paymentLinkId) });
                                    }}
                                >
                                    {translate('Cancel')}
                                </Button>
                            );
                        case 'REFUND_PAYMENT':
                            return (
                                <Button
                                    onClick={async event => {
                                        event.stopPropagation();
                                        setCancelPaymentLinkDialogState({ open: true, paymentLink: paymentLinks.find(paymentLink => paymentLink.paymentLinkId === paymentLinkId) });
                                    }}
                                >
                                    {translate('Refund')}
                                </Button>
                            );
                        case 'NA':
                        default:
                            return null;
                    }
                },
                setCellProps: () => {
                    return {
                        style: { paddingLeft: 0, paddingRight: 0 },
                    };
                },
            },
        },
    ];

    return (
        <div style={{ position: 'relative' }}>
            <UpdatingContentProgress loading={loading} />
            <CancelPaymentLinkDialog
                open={cancelPaymentLinkDialogState.open}
                paymentLink={cancelPaymentLinkDialogState.paymentLink}
                onClose={() => setCancelPaymentLinkDialogState({ ...cancelPaymentLinkDialogState, open: false })}
                onExit={() => setCancelPaymentLinkDialogState({ ...cancelPaymentLinkDialogState, paymentLink: undefined })}
                onSuccess={() => {
                    setCancelPaymentLinkDialogState({ ...cancelPaymentLinkDialogState, open: false });
                    load();
                }}
            />
            <MUIDataTable
                ref={table}
                className={classes.table}
                title={translate('@company - Payment Links', { company: appContext?.companyName })}
                data={paymentLinks.map(paymentLink => {
                    return {
                        paymentLinkId: paymentLink.paymentLinkId,
                        createdAt: formatAsReadableDateTime(paymentLink.createdAt),
                        paymentLinkStatus: translate(`PaymentLinkStatuses.${paymentLink.paymentLinkStatus}`),
                        reason: paymentLink.reason,
                        customerName: paymentLink.customerName,
                        total: formatAsNumberCurrency(paymentLink.total),
                        url: paymentLink.url,
                    };
                })}
                columns={columns}
                options={{
                    responsive: 'standard',
                    selectableRows: 'none',
                    filterType: 'multiselect',
                    rowsPerPage: 10,
                    customToolbar: () => {
                        return (
                            <>
                                <RefreshToolbarButton onClick={load} />
                                <Link to='link/new'>
                                    <CreateToolbarButton tooltip={translate('Create new Payment Link')} />
                                </Link>
                            </>
                        );
                    },
                    textLabels: translatedTextLabels(),
                    onRowClick: (rowData: Array<any>, rowMeta: { dataIndex: number, rowIndex: number }) => {
                        const paymentLinkId: PaymentLinkId = rowData[0];
                        navigate(`link/${paymentLinkId}`);
                    },
                }}
            />
        </div>
    );
}

function getPaymentLinkCancelType(paymentLink?: PaymentLinkVm): 'CANCEL_PAYMENT_LINK' | 'CANCEL_PAYMENT' | 'REFUND_PAYMENT' | 'NA' {
    let twentyFourHoursAgo = moment().subtract(24, 'hours');
    if (paymentLink?.paymentLinkStatus === PaymentLinkStatuses.ACTIVE) {
        return 'CANCEL_PAYMENT_LINK';
    }
    if (paymentLink?.paymentLinkStatus === PaymentLinkStatuses.PAID) {
        if (paymentLink?.paidAt && moment(paymentLink.paidAt).isBefore(twentyFourHoursAgo)) {
            return 'REFUND_PAYMENT';
        }
        if (paymentLink?.paidAt && moment(paymentLink.paidAt).isAfter(twentyFourHoursAgo)) {
            return 'CANCEL_PAYMENT';
        }
    }
    return 'NA';
}

const useStyles = makeStyles(theme => ({
    table: {
        whiteSpace: 'nowrap',
        '& tr': { cursor: 'pointer' },
        '& tr:hover': { backgroundColor: 'rgba(0, 0, 0, 0.07) !important' },
    },
    toolbar: {
        paddingRight: theme.spacing(3),
    },
}));
